// external imports
import React, { Fragment, useEffect, useState, useRef } from 'react';
import { API } from 'aws-amplify';
import { useDispatch, useSelector } from 'react-redux';
import DocumentTitle from 'react-document-title';
import { CSVLink } from 'react-csv';
import {
	Button,
	Col,
	Container,
	CustomInput,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Input,
	Row,
	Table,
	UncontrolledDropdown
} from 'reactstrap';
import { area, polygon } from '@turf/turf';

// Local imports
import {
	fetchRegions,
	fetchSites,
	fetchCountries,
	fetchSiteTypes,
	fetchCommodities,
	getAuthenticatedUserAttributes
} from 'actions';
import { Crumbs } from 'containers';
import config from 'config';
import {
	getMapSiteLayers2,
	createCSVDownloadOfSites,
	changeChevron,
	commodityColorExpression,
	commodityColors
} from './Helpers';
import { AppMap } from './Map';
import CreateAndUpdateSiteModal from 'components/modals/CreateAndUpdateSiteModal';
import DeleteSiteModal from 'components/modals/DeleteSiteModal';
import { renderCommodityLegend } from './map/CommodityLegend';

// Map imports - don't remove these or map won't appear
//import { area, centerOfMass, polygon } from '@turf/turf';
// import ReactMapboxGl, { Layer, Source } from 'react-mapbox-gl';
// import DrawControl from 'react-mapbox-gl-draw';

// import MapboxDraw from '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'; // eslint-disable-line no-unused-vars
// import mapboxGlDraw from 'mapbox-gl-draw'; // eslint-disable-line no-unused-vars
// import { map } from 'jquery'; // eslint-disable-line no-unused-vars
import 'mapbox-gl/src/css/mapbox-gl.css';

import './Sites.scss';
import mapboxgl from 'mapbox-gl';
import { replacer } from 'utils';

export const mainColor = '#d8704b';

mapboxgl.workerClass =
	require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default; // eslint-disable-line

const INITIAL_FILTERS = {
	typeFilter: '',
	nameFilter: '',
	idFilter: '',
	siteTypeFilter: '',
	countryFilter: '',
	commodityFilter: '',
	plantTypeFilter: '',
	parentSiteFilter: ''
};

export const INITIAL_DRAWING = {
	drawAOI: null,
	drawError: null,
	drawArea: null,
	drawLat: null,
	drawLong: null,
	drawCountry: null,
	drawRegion: null,
	currentZoom: null,
	kmlGeojson: null,
	tileLayer: 'light-v11'
};

export const INITIAL_LIST_MAP_PROPS = {
	lat: 38,
	long: 11,
	zoom: [4],
	tileLayer: 'light-v11'
};

export const INITIAL_FORM_VALUES = {
	id: '',
	name: '',
	parent_id: '',
	country: '',
	region: '',
	company: '',
	lat: null,
	lon: null,
	commodity_id: null,
	site_type_id: null,
	stype: null,
	parent_type_id: null,
	params: {
		id: null,
		twin_id: null,
		capacity: null,
		end_date: null,
		use_smoke: '',
		co2_factor: null,
		plant_type: null,
		prod_state: null,
		start_date: ''
	}
};

const plantTypes = ['BF/BOF', 'EAF'];
const rowsPerPage = 30;

export const Sites = () => {
	const dispatch = useDispatch();

	const mapRef = useRef(null);
	const popupRef = useRef(null);

	const [siteFilters, setSiteFilters] = useState(INITIAL_FILTERS);
	const [isAdmin, setIsAdmin] = useState(null);
	const [showModal, setShowModal] = useState(null);
	const [formValue, setFormValue] = useState(null);

	const [drawing, setDrawing] = useState(INITIAL_DRAWING);
	const [listMapProps, setListMapProps] = useState(INITIAL_LIST_MAP_PROPS);

	// endpoint state
	const sites = useSelector((state) => state.sites);
	const regions = useSelector((state) => state.regions);
	const countries = useSelector((state) => state.countries);
	const siteTypes = useSelector((state) => state.siteTypes);
	const commodities = useSelector((state) => state.commodities);

	// pages
	const [currentPage, setCurrentPage] = useState(1);
	const startIndex = (currentPage - 1) * rowsPerPage;
	const endIndex = startIndex + rowsPerPage;

	// site specific
	const [selectedSite, setSelectedSite] = useState(null);
	const [updateSite, setUpdateSite] = useState(null);
	const [parentSite, setParentSite] = useState(null);
	const [deleteSite, setDeleteSite] = useState(null);

	let [datetime, csv_sites] = createCSVDownloadOfSites(
		sites,
		commodities,
		siteTypes
	);

	useEffect(() => {
		dispatch(fetchSites());
		dispatch(fetchRegions());
		dispatch(fetchCountries());
		dispatch(fetchSiteTypes());
		dispatch(fetchCommodities());

		const checkAdmin = async () => {
			const user = await getAuthenticatedUserAttributes();
			setIsAdmin(user.userRole === 'admin' || user.userRole === 'superUser');
		};

		checkAdmin();
	}, []);

	const filterSites = (unfilteredSites) => {
		const {
			parentSiteFilter,
			idFilter,
			countryFilter,
			nameFilter,
			commodityFilter,
			siteTypeFilter,
			plantTypeFilter
		} = siteFilters;

		let sites = unfilteredSites.map((site) => ({
			...site,
			subsites:
				site.parent_id === null
					? unfilteredSites.filter((subsite) => subsite.parent_id === site.id)
					: [],
			plant_type: site.params?.plant_type ?? site.plant_type
		}));

		let sub_sites = [];

		if (plantTypeFilter !== '') {
			sites = sites.filter((site) => {
				return site.hasOwnProperty('plant_type');
			});

			sites = sites.filter(({ plant_type }) => {
				return plant_type?.includes(plantTypeFilter);
			});
		}

		if (nameFilter !== '') {
			sites = sites.filter(({ name }) => {
				return name.toLowerCase()?.includes(nameFilter.toLowerCase());
			});
		}

		if (sub_sites.includes(parseInt(idFilter))) {
			let parentSite = sites.filter((site) => site.id === parseInt(idFilter))[0]
				.parent_id;

			sites = sites.filter(({ id }) => {
				return id.toString()?.includes(parentSite);
			});
		} else {
			sites = sites.filter(({ id }) => {
				return id.toString()?.includes(idFilter.toLowerCase());
			});
		}

		if (countryFilter !== '') {
			sites = sites.filter(({ country }) => {
				return country.toLowerCase()?.includes(countryFilter.toLowerCase());
			});
		}

		if (siteTypeFilter !== '') {
			sites = sites.filter(({ site_type_id }) => {
				return site_type_id === parseInt(siteTypeFilter);
			});
		}

		if (commodityFilter !== '') {
			sites = sites.filter(({ commodity_id }) => {
				return commodity_id === parseInt(commodityFilter);
			});
		}

		if (parentSiteFilter !== '') {
			if (parentSiteFilter === 'main-sub') {
				sites = sites.filter(({ subsites }) => {
					return subsites.length > 0;
				});
			} else if (parentSiteFilter === 'main-no-sub') {
				sites = sites.filter(({ subsites }) => {
					return subsites.length === 0;
				});
			}
		}

		const renderableSites = sites.filter((site) => site.parent_id === null);

		return renderableSites;
	};

	const handleFilterChange = (e) => {
		const { name, value } = e.target;

		setSiteFilters((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const handleZoomToSite = (lat, long, zoom, site) => {
		if (!selectedSite || selectedSite !== site) {
			setSelectedSite(site);

			setSelectedSite();
		}
		mapRef.current.recenter([long, lat], zoom);
	};

	const handleMouseOverSite = (feature, e, map) => {
		if (!feature) return;

		if (popupRef.current) {
			popupRef.current.remove();
		}

		popupRef.current = new mapboxgl.Popup({
			closeOnClick: false,
			closeButton: false
		});

		const { id, name } = feature.properties;

		map.getCanvas().style.cursor = 'pointer';

		popupRef.current
			.setLngLat([e.lngLat.lng, e.lngLat.lat])
			.setHTML(
				`
           <div class="map-popup">
        		<div class="map-popup__id">Site ${id}</div>
        		<div class="map-popup__name">${name}</div>
    		</div>
        `
			)
			.addTo(map);
	};

	const handleMouseLeaveSite = (feature, e, map) => {
		map.getCanvas().style.cursor = '';

		if (popupRef.current) {
			popupRef.current.remove();
		}
		popupRef.current = null;
	};

	const handleClickOnSiteOnMap = (feature, e, map) => {
		const { id, name } = feature.properties;

		const site = sites.find((s) => s.id === id);

		if (!site) return;

		setSelectedSite(site);
		setListMapProps((prev) => ({
			...prev,
			lat: site.lat,
			long: site.lon
		}));

		// open update site modal
		toggleUpdate(site);
	};

	const toggleCreate = () => {
		if (showModal === 'create') {
			console.log('showModal', showModal);
			setShowModal(null);
			setParentSite(null);
			setUpdateSite(null);
			setSiteFilters(INITIAL_FILTERS);
		} else {
			setShowModal('create');
		}

		setDrawing(INITIAL_DRAWING);
		setFormValue(INITIAL_FORM_VALUES);
	};

	const toggleDelete = (site) => {
		if (showModal === 'delete') {
			setShowModal(null);
		} else {
			setShowModal('delete');
		}

		setDeleteSite(site);
	};

	const toggleUpdate = async (site) => {
		setUpdateSite(site);

		const siteAOIs = await getSiteInfo(site.id);
		let parentAOIs;
		if (site.parent_id) {
			parentAOIs = await getSiteInfo(site.parent_id);
		}

		const thisSiteAOI = siteAOIs.find((s) => s.id === site.id);
		const subsiteAOIS = !site.parent_id
			? siteAOIs.filter((s) => s.id !== site.id) || []
			: parentAOIs.filter((s) => s.id !== site.parent_id && s.id !== site.id) ||
				[];
		let parentAOI = parentAOIs?.find((s) => s.id === site.parent_id);

		if (!thisSiteAOI || !thisSiteAOI.aoi) return;

		// do this in the capacity table
		// await this.props.fetchSiteCapacity({ id: site['id'] });
		console.log(thisSiteAOI);

		const siteDrawValues = {
			drawAOI: {
				type: 'Polygon',
				coordinates: thisSiteAOI.aoi.coordinates[0]
			},
			drawLat: site.lat,
			drawLong: site.lon,
			drawArea: area(polygon(thisSiteAOI.aoi.coordinates[0])) / 1000000,
			drawCountry: countries.find((c) => c.name === site.country)?.id,
			drawRegion: regions.find((r) => r.name === site.region)?.id,
			tileLayer: 'light-v11',
			endpointAOI: thisSiteAOI
		};

		// updating a subsite
		if (site.parent_id) {
			setParentSite({
				site: sites.find((s) => s.id === site.parent_id),
				aoi: parentAOI?.aoi,
				subsiteAOIs: subsiteAOIS
			});
		}
		setSiteFilters(INITIAL_FILTERS); // clear any applied filters otherwise other sites don't show up in modal
		setDrawing(siteDrawValues);
		setFormValue(site);

		if (showModal === 'create') {
			setShowModal(null);
		} else {
			setShowModal('create');
		}
	};

	const getSiteInfo = async (id) => {
		return API.get(
			config.gainsApi.NAME,
			replacer(config.gainsApiEndpoints.siteInfo, { id }),
			{
				headers: {}
			}
		);
	};

	const toggleCreateSubsite = async (parentSite) => {
		const allAOIs = await getSiteInfo(parentSite.id);
		const parentAOI = allAOIs.find((aoi) => aoi.id === parentSite.id)?.aoi;
		const subsiteAOIs = allAOIs
			.filter((aoi) => aoi.id !== parentSite.id)
			?.map((aoi) => aoi);

		setDrawing(INITIAL_DRAWING);
		setParentSite({
			site: parentSite,
			aoi: parentAOI,
			subsiteAOIs: subsiteAOIs
		});
		setListMapProps({
			lat: parentSite?.lat,
			long: parentSite.lon,
			zoom: 12
		});
		setFormValue({
			...INITIAL_FORM_VALUES,
			parent_id: parentSite.id,
			parent_type_id: parentSite.site_type_id
		});

		if (showModal === 'create') {
			setShowModal(null);
		} else {
			setShowModal('create');
		}
	};

	console.log('formValues', formValue);

	const renderResultsTable = () => {
		const renderButtons = () => {
			return (
				<Row className="py-3 justify-content-center" noGutters>
					<Col>
						<div className="d-flex justify-content-center">
							{isAdmin && (
								<Button
									size="md"
									color="info"
									className="mr-2"
									onClick={toggleCreate}
								>
									<i className="fas fa-plus mr-2" />
									Add site
								</Button>
							)}
							<Button
								size="md"
								color="info"
								className="mr-2"
								onClick={() => setSiteFilters(INITIAL_FILTERS)}
							>
								<i className="fa-solid fa-filter mr-2" />
								Clear all filters
							</Button>
							{isAdmin && (
								<CSVLink
									data={csv_sites}
									filename={`sites-list-download-${datetime}.csv`}
									className="btn btn-info mr-2"
									target="_blank"
								>
									<i className="fa-solid fa-download mr-2" />
									Download sites as csv
								</CSVLink>
							)}
							<Button
								size="md"
								outline
								color="secondary"
								onClick={() => dispatch(fetchSites())}
								title="Refresh sites"
							>
								<i className="fas fa-sync-alt" />
								<span className="sr-only">Refresh</span>
							</Button>
						</div>
					</Col>
				</Row>
			);
		};

		const renderMap = () => {
			const renderTileLayerBtns = () => {
				return (
					<div id="mapStyleMenu">
						<Row className="d-flex justify-content-center p-0 m-0">
							{config.mapbox_styles.map(({ stylename, url }, idx) => (
								<div key={`key${idx}`} className="py-2 m-0">
									<label className="px-1 m-0">
										<Input
											className="py-0 mapRadios"
											id="drawStyle"
											type="radio"
											name="rtoggle"
											value={url}
											defaultChecked={listMapProps.tileLayer === url}
											onChange={() =>
												setListMapProps((prev) => ({ ...prev, tileLayer: url }))
											}
										></Input>
										<img
											className="map-radio-imgs"
											src={require(`images/${stylename}.png`)}
											alt={stylename}
										></img>
									</label>
								</div>
							))}
						</Row>
					</div>
				);
			};

			const renderTheMap = () => {
				const filteredSites = filterSites(sites);
				const mapboxJSONs = getMapSiteLayers2(filteredSites, siteTypes);

				const layers = !sites?.length
					? []
					: [
							{
								id: 'smelters',
								type: 'circle',
								data: mapboxJSONs.smelter,
								paint: {
									'circle-color': commodityColorExpression,
									'circle-radius': 5
								}
							},
							{
								id: 'stockpiles',
								type: 'circle',
								data: mapboxJSONs.stockpiles,
								paint: {
									'circle-color': commodityColorExpression,
									'circle-radius': 5
								}
							},
							{
								id: 'airfields',
								type: 'circle',
								data: mapboxJSONs.airfield,
								paint: {
									'circle-color': commodityColorExpression,
									'circle-radius': 5
								}
							}
						];

				return (
					<AppMap
						ref={mapRef}
						tileLayer={listMapProps.tileLayer}
						layers={layers}
						center={[listMapProps.long, listMapProps.lat]}
						zoom={listMapProps.zoom}
						onFeatureClick={handleClickOnSiteOnMap}
						onFeatureMouseEnter={handleMouseOverSite}
						onFeatureMouseLeave={handleMouseLeaveSite}
					/>
				);
			};

			return (
				<Row noGutters>
					<Col>
						<div>
							{renderTileLayerBtns()}

							{renderCommodityLegend(commodities, siteFilters, setSiteFilters)}

							{renderTheMap()}
						</div>
					</Col>
				</Row>
			);
		};

		const renderSitesList = () => {
			if (!sites.length)
				return (
					<>
						<h4 className="text-center mt-3">No sites found</h4>
					</>
				);

			const {
				parentSiteFilter,
				idFilter,
				countryFilter,
				nameFilter,
				commodityFilter,
				siteTypeFilter,
				plantTypeFilter
			} = siteFilters;

			const filteredSites = filterSites(sites);
			//const sitesWithAnAOI = filteredSites.filter((site) => site.lat && site.lon)
			const paginatedSites = filteredSites.slice(startIndex, endIndex);
			//const paginatedSites = filteredSites.filter((s) => s.id === 999369)
			const totalPages = Math.ceil(filteredSites.length / rowsPerPage);

			const renderFilterRow = () => {
				return (
					<tr id="filterRow">
						<th style={{ width: '30px' }}>
							<CustomInput
								id="parentSiteFilter"
								name="parentSiteFilter"
								type="select"
								value={parentSiteFilter}
								onChange={(e) => handleFilterChange(e)}
								className="mr-2 parent-filter"
								title="Filter by parent/subsites"
								bsSize="sm"
							>
								<option value="main" key="main">
									Main sites
								</option>
								<option value="main-sub" key="main-sub">
									Main sites with subsites
								</option>
								<option value="main-no-sub" key="main-no-sub">
									Main sites without subsites
								</option>
							</CustomInput>
						</th>
						<th style={{ width: '30px' }}>
							<CustomInput
								id="idFilter"
								name="idFilter"
								type="text"
								className="mr-2 name-filter"
								onChange={(e) => handleFilterChange(e)}
								value={idFilter}
								placeholder="ID search"
								bsSize="sm"
								inline
							/>
						</th>
						<th>
							<CustomInput
								id="countryFilter"
								name="countryFilter"
								type="select"
								value={countryFilter}
								onChange={(e) => handleFilterChange(e)}
								className="mr-2 name-filter"
								bsSize="sm"
							>
								<option value="">Country search</option>
								{countries.map(({ id, name }, idx) => (
									<option value={name} key={`country${idx}`}>
										{name}
									</option>
								))}
							</CustomInput>
						</th>
						<th>
							<CustomInput
								id="nameFilter"
								name="nameFilter"
								type="text"
								className="mr-2 name-filter"
								onChange={(e) => handleFilterChange(e)}
								value={nameFilter}
								placeholder="Name search"
								bsSize="sm"
								inline
							/>
						</th>

						<th>
							<CustomInput
								id="commodityFilter"
								name="commodityFilter"
								type="select"
								value={commodityFilter}
								onChange={(e) => handleFilterChange(e)}
								className="mr-2 name-filter"
								bsSize="sm"
							>
								<option value="">Commodity search</option>
								{commodities.map(({ id, name, symbol }, idx) => (
									<option value={id} key={`comm${idx}`}>
										{symbol}
									</option>
								))}
							</CustomInput>
						</th>

						<th>
							<CustomInput
								id="siteTypeFilter"
								name="siteTypeFilter"
								type="select"
								value={siteTypeFilter}
								onChange={(e) => handleFilterChange(e)}
								className="mr-2 site-type-filter"
								bsSize="sm"
							>
								<option value="">Site Type</option>
								<option value="1">smelter</option>
								<option value="2">airfield</option>
								<option value="3">stockpiles</option>
							</CustomInput>
						</th>
						<th>
							<CustomInput
								id="plantTypeFilter"
								name="plantTypeFilter"
								type="select"
								value={plantTypeFilter}
								onChange={(e) => handleFilterChange(e)}
								className="mr-2 plant-type-filter"
								bsSize="sm"
							>
								<option value="">Plant Type</option>
								{plantTypes.map((type, idx) => (
									<option value={type} key={`plant${idx}`}>
										{type}
									</option>
								))}
							</CustomInput>
						</th>

						<th style={{ textAlign: 'end' }}>
							<Button
								size="sm"
								title="First page"
								color="info"
								className="mr-2"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage(1)}
							>
								<i className="fas fa-angles-left" />
							</Button>
							<Button
								size="sm"
								title="Previous page"
								color="info"
								className="mr-2"
								disabled={currentPage === 1}
								onClick={() => setCurrentPage((prev) => prev - 1)}
							>
								<i className="fas fa-chevron-left" />
							</Button>
							<Button
								size="sm"
								color="info"
								title="Next page"
								className="mr-2"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage((prev) => prev + 1)}
							>
								<i className="fas fa-chevron-right" />
							</Button>
							<Button
								size="sm"
								title="Last page"
								color="info"
								className="mr-2"
								disabled={currentPage === totalPages}
								onClick={() => setCurrentPage(totalPages)}
							>
								<i className="fas fa-angles-right" />
							</Button>
							<span>
								Page {currentPage}/{totalPages}
							</span>
						</th>
					</tr>
				);
			};

			const renderListOfSites = () => {
				return (
					<Row noGutters>
						<Col xs="12">
							<Table className="sitesListTable" id="sitesPageTable">
								<thead>
									{renderFilterRow()}
									<tr>
										<th style={{ width: '30px' }}>Subsites</th>
										<th style={{ width: '15px' }}>ID</th>
										<th style={{ width: '30px' }}>Country</th>
										<th style={{ width: '50px' }}>Site name</th>
										<th>Commodity</th>
										<th>Site Type</th>
										<th>Plant Type</th>
										<th style={{ textAlign: 'end' }}>{sites.length} sites</th>
									</tr>
								</thead>

								<tbody>
									{paginatedSites.map((site, ind) => {
										const {
											id,
											name,
											country,
											site_type_id,
											commodity_id,
											plant_type,
											subsites,
											parent_id,
											lat,
											lon
										} = site;
										const dspInd = ind + 1;

										if (parent_id === null && subsites?.length > 0) {
											return (
												<>
													<tr
														key={`region${dspInd}`}
														style={{
															backgroundColor:
																selectedSite?.id === id &&
																'rgba(247, 247, 247, 0.85)'
														}}
													>
														<td style={{ width: '75px' }}>
															<i
																className="expand-button fa-solid fa-caret-down pr-2"
																data-toggle="collapse"
																data-target={`#accordian${dspInd}`}
																style={{ cursor: 'pointer' }}
																id={`id${ind}`}
																onClick={(e) => changeChevron(e, ind)}
																title="Expand to see child sites"
															></i>
															({subsites.length})
														</td>
														<td
															style={{
																width: '15px',
																textDecoration: 'underline',
																cursor: 'pointer'
															}}
															title="Zoom to site"
															onClick={() => handleZoomToSite(lat, lon, 12)}
														>
															{id}
														</td>
														<td>{country}</td>
														<td style={{ cursor: 'pointer' }}>{name}</td>
														{commodities
															.filter((comm) => comm.id === commodity_id)
															.map((filteredComm) => (
																<td>
																	<div
																		style={{
																			display: 'flex',
																			alignItems: 'center',
																			gap: '3px'
																		}}
																	>
																		<div
																			style={{
																				width: '8px',
																				height: '8px',
																				borderRadius: '50%',
																				backgroundColor:
																					commodityColors[commodity_id],
																				marginRight: '4px',
																				paddingTop: '4px'
																			}}
																		/>
																		{filteredComm.symbol}
																	</div>
																</td>
															))}

														{siteTypes
															.filter((comm) => comm.id === site_type_id)
															.map((filteredComm) => (
																<td>{filteredComm.name}</td>
															))}
														<td>{plant_type}</td>
														<td>
															<div className="d-flex justify-content-end">
																{isAdmin && (
																	<UncontrolledDropdown size="sm">
																		<DropdownToggle className="pointered btn-light">
																			<i className="fas fa-bars" />
																			<span className="sr-only">Actions</span>
																		</DropdownToggle>
																		<DropdownMenu>
																			<DropdownItem
																				className="pointered"
																				onClick={() =>
																					lat && lon
																						? toggleUpdate(site)
																						: undefined
																				}
																				title={
																					lat && lon
																						? 'Update details of this site'
																						: 'This site does not have an AOI'
																				}
																			>
																				<i className="fas fa-edit fa-fw mr-2" />
																				Update
																			</DropdownItem>
																			{site.stype === 'smelter' &&
																				site.commodity_id === 3 && (
																					<DropdownItem
																						className="pointered"
																						onClick={() =>
																							toggleCreateSubsite(site)
																						}
																					>
																						<i className="fas fa-house-fire fa-fw mr-2" />
																						Add subsite
																					</DropdownItem>
																				)}

																			<DropdownItem
																				className="pointered"
																				onClick={() => toggleDelete(site)}
																			>
																				<i className="fas fa-trash-alt fa-fw mr-2" />
																				Delete
																			</DropdownItem>
																		</DropdownMenu>
																	</UncontrolledDropdown>
																)}
															</div>
														</td>
													</tr>

													{subsites.map((site, idx) => (
														<tr
															key={idx}
															id={`accordian${dspInd}`}
															className="collapse"
															style={{
																backgroundColor:
																	selectedSite?.id === id &&
																	'rgba(247, 247, 247, 0.85)'
															}}
														>
															<td className="subsitesStyle">
																{site.params.parent_id}
															</td>
															<td
																className="subsitesStyle"
																style={{
																	textDecoration: 'underline',
																	cursor: 'pointer'
																}}
																title="Zoom to site"
																onClick={() => handleZoomToSite(lat, lon, 12)}
															>
																{site.id}
															</td>
															<td className="subsitesStyle">{site.country}</td>
															<td className="subsitesStyle">{site.name}</td>

															{commodities
																.filter((comm) => comm.id === site.commodity_id)
																.map((filteredComm) => (
																	<td className="subsitesStyle">
																		<div
																			style={{
																				display: 'flex',
																				alignItems: 'center',
																				gap: '3px'
																			}}
																		>
																			<div
																				style={{
																					width: '8px',
																					height: '8px',
																					borderRadius: '50%',
																					backgroundColor:
																						commodityColors[site.commodity_id],
																					marginRight: '4px',
																					paddingTop: '4px'
																				}}
																			/>
																			{filteredComm.symbol}
																		</div>
																	</td>
																))}

															{siteTypes
																.filter((comm) => comm.id === site.site_type_id)
																.map((filteredComm) => (
																	<td className="subsitesStyle">
																		{filteredComm.name}
																	</td>
																))}

															<td className="subsitesStyle">
																{site.plant_type}
															</td>
															<td className="subsitesStyle">
																<div className="d-flex justify-content-end">
																	{isAdmin && (
																		<UncontrolledDropdown size="sm">
																			<DropdownToggle className="pointered btn-light">
																				<i className="fas fa-bars" />
																				<span className="sr-only">Actions</span>
																			</DropdownToggle>
																			<DropdownMenu>
																				<DropdownItem
																					className="pointered"
																					onClick={() =>
																						lat && lon
																							? toggleUpdate(site)
																							: undefined
																					}
																					title={
																						lat && lon
																							? 'Update details of this site'
																							: 'This site does not have an AOI'
																					}
																				>
																					<i className="fas fa-edit fa-fw mr-2" />
																					Update
																				</DropdownItem>
																				<DropdownItem
																					className="pointered"
																					onClick={() => toggleDelete(site)}
																				>
																					<i className="fas fa-trash-alt fa-fw mr-2" />
																					Delete
																				</DropdownItem>
																			</DropdownMenu>
																		</UncontrolledDropdown>
																	)}
																</div>
															</td>
														</tr>
													))}
												</>
											);
										} else if (parent_id === null && subsites?.length === 0) {
											return (
												<tr
													key={`region${dspInd}`}
													style={{
														backgroundColor:
															selectedSite?.id === id &&
															'rgba(247, 247, 247, 0.85)'
													}}
												>
													<td></td>
													<td
														style={{
															width: '15px',
															textDecoration: 'underline',
															cursor: 'pointer'
														}}
														title="Zoom to site"
														onClick={() => handleZoomToSite(lat, lon, 12)}
													>
														{id}
													</td>
													<td>{country}</td>
													<td style={{ cursor: 'pointer' }}>{name}</td>
													{commodities
														.filter((comm) => comm.id === commodity_id)
														.map((filteredComm, idx) => (
															<td key={idx}>
																<div
																	style={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: '3px'
																	}}
																>
																	<div
																		style={{
																			width: '8px',
																			height: '8px',
																			borderRadius: '50%',
																			backgroundColor:
																				commodityColors[commodity_id],
																			marginRight: '4px',
																			paddingTop: '4px'
																		}}
																	/>
																	{filteredComm.symbol}
																</div>
															</td>
														))}

													{siteTypes
														.filter((comm) => comm.id === site_type_id)
														.map((filteredComm, idx) => (
															<td key={idx}>{filteredComm.name}</td>
														))}
													<td>{plant_type}</td>
													<td>
														<div className="d-flex justify-content-end">
															{isAdmin && (
																<UncontrolledDropdown size="sm">
																	<DropdownToggle className="pointered btn-light">
																		<i className="fas fa-bars" />
																		<span className="sr-only">Actions</span>
																	</DropdownToggle>
																	<DropdownMenu>
																		<DropdownItem
																			className="pointered"
																			onClick={() =>
																				lat && lon
																					? toggleUpdate(site)
																					: undefined
																			}
																			title={
																				lat && lon
																					? 'Update details of this site'
																					: 'This site does not have an AOI'
																			}
																		>
																			<i className="fas fa-edit fa-fw mr-2" />
																			Update
																		</DropdownItem>
																		{site.stype === 'smelter' &&
																			site.commodity_id === 3 && (
																				<DropdownItem
																					className="pointered"
																					onClick={() =>
																						toggleCreateSubsite(site)
																					}
																				>
																					<i className="fas fa-house-fire fa-fw mr-2" />
																					Add subsite
																				</DropdownItem>
																			)}
																		<DropdownItem
																			className="pointered"
																			onClick={() => toggleDelete(site)}
																		>
																			<i className="fas fa-trash-alt fa-fw mr-2" />
																			Delete
																		</DropdownItem>
																	</DropdownMenu>
																</UncontrolledDropdown>
															)}
														</div>
													</td>
												</tr>
											);
										} else return null;
									})}
								</tbody>
							</Table>
						</Col>
					</Row>
				);
			};

			return <>{renderListOfSites()}</>;
		};

		return (
			<div>
				{renderButtons()}

				{renderMap()}

				{renderSitesList()}
			</div>
		);
	};

	const renderModals = () => {
		return (
			<Fragment>
				<CreateAndUpdateSiteModal
					isOpen={showModal === 'create'}
					toggleCreate={toggleCreate}
					drawing={drawing}
					setDrawing={setDrawing}
					formValue={formValue}
					setFormValue={setFormValue}
					parentSite={parentSite}
					listMapProps={listMapProps}
					filteredSites={filterSites(sites)}
					siteFilters={siteFilters}
					setSiteFilters={setSiteFilters}
					updateSite={updateSite}
					setUpdateSite={setUpdateSite}
				/>

				<DeleteSiteModal
					isOpen={showModal === 'delete'}
					toggleDelete={toggleDelete}
					deleteSite={deleteSite}
					setDeleteSite={setDeleteSite}
				/>
			</Fragment>
		);
	};

	return (
		<DocumentTitle title="Gains Admin | Sites">
			<Container fluid>
				<Crumbs path={[{ title: 'Sites' }]} />

				{renderResultsTable()}

				{renderModals()}
			</Container>
		</DocumentTitle>
	);
};
